import { createRouter, createWebHistory  } from 'vue-router'
import { userPost } from "@/utils/request.js"
import { getForumToken, captureForumSession, isCurrentForumSession } from '@/utils/forumSession.js'
import { decodeParam, encodeParam } from "@/utils/utils.js"
import {
  getWeeklyActivitySubType,
  queryProgressWeeklyActivityId,
} from "@/utils/weeklyActivity.js"
import {
  beginMeetingJoinSession,
  getMeetingOnceKey,
  getRouteForumId,
  setMeetingTrackContext,
  trackMeetingPoint,
} from "@/utils/trackPoint.js"

const NO_PERMISSION_MESSAGE = "No Permission!"
const MEETING_OPERATOR_KEY = "meetingOperator"

function cacheMeetingOperator(info = {}) {
  const employee = info.employeeInfo ?? info.employee ?? info.userInfo ?? info.user ?? info
  const operator = {
    operatorId: employee.employeeId ?? employee.userId ?? employee.id ?? "",
    operatorName: employee.employeeName ?? employee.userName ?? employee.name ?? "",
  }

  try {
    sessionStorage.setItem(MEETING_OPERATOR_KEY, JSON.stringify(operator))
  } catch {
    return
  }
}

function getWelcomeRedirect(to, options = {}) {
  const query = { ...to.query }

  if (options.stripTokenQuery) {
    delete query.t
    delete query.token
  }

  return {
    name: "welcome",
    params: {
      params: to.params.params,
    },
    query: {
      ...query,
      redirect: to.fullPath,
    },
  }
}

function isNoPermission(payload) {
  return payload?.message === NO_PERMISSION_MESSAGE
}

function isSuccessResponse(payload) {
  return payload?.code === 200 || payload?.code === 0
}

function getEmployeeInfo(payload) {
  return payload?.result || payload?.data
}

async function getEmployeeAccessStatus() {
  const expected = captureForumSession()
  try {
    const res = await userPost("/matrix-search/forum/employee/info", {})
    if (!isCurrentForumSession(expected)) return "changed"

    if (isNoPermission(res)) {
      return "denied"
    }

    const employeeInfo = getEmployeeInfo(res)
    if (isSuccessResponse(res) && employeeInfo?.isEmployee === true) {
      cacheMeetingOperator(employeeInfo)
      return "allowed"
    }

    return "denied"
  } catch (err) {
    if (err?.isStaleSession) return "changed"
    if (
      err?.isUnauthorized ||
      err?.response?.status === 401
    ) {
      return "expired"
    }

    return "denied"
  }
}

async function resolveWeeklyActivityRoute(to, subType) {
  try {
    const forumId = await queryProgressWeeklyActivityId(subType)
    if (!forumId) return "/weeklyActivityError"

    return {
      name: to.name,
      params: {
        ...to.params,
        params: encodeParam(JSON.stringify(forumId)),
      },
      query: to.query,
      replace: true,
    }
  } catch (err) {
    if (err?.isStaleSession || err?.isUnauthorized || err?.response?.status === 401) {
      return false
    }
    return "/error?type=0"
  }
}

function hasValidVisitorParams(encodedParams) {
  if (typeof encodedParams !== "string" || !encodedParams) return false

  try {
    const params = JSON.parse(decodeParam(encodedParams))
    return Boolean(
      params &&
      typeof params === "object" &&
      !Array.isArray(params) &&
      params.forumId
    )
  } catch {
    return false
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [{
    path: "/login",
    name: "login",
    component: () => import("@/pages/welcome.vue"),
    props: { loginOnly: true },
  },{
    path: "/visitor/:params",
    name: "visitor",
    component: () => import("@/pages/visitor.vue")
  },{
    path: "/host/:params",
    name: "host",
    component: () => import("@/pages/host.vue")
  },{
    path: "/endPage",
    name: "end",
    component: () => import("@/pages/end.vue")
  },{
    path: "/app",
    name: "app",
    component: () => import("@/pages/app.vue")
  },{
    path: "/welcomePage/:params",
    name: "welcome",
    component: () => import("@/pages/welcome.vue")
  },{
    path: "/appPage/:params",
    name: "appPage",
    component: () => import("@/pages/appPage.vue")
  },{
    path: "/moderator/:params",
    name: "moderator",
    component: () => import("@/pages/moderator.vue")
  },{
    path: "/weeklyActivityError",
    name: "weeklyActivityError",
    component: () => import("@/pages/weeklyActivityError.vue")
  },{
    path: "/error",
    name: "error",
    component: () => import("@/pages/error.vue")
  },{
    path: "/:pathMatch(.*)*",
    redirect: "/error", // 直接跳转到 error 页面
  },
  ],
})

router.beforeEach(async (to) => {
  if (to.name === "visitor" && !hasValidVisitorParams(to.params.params)) {
    return {
      name: "error",
      query: {
        type: "0",
        ...(to.query.lang ? { lang: to.query.lang } : {}),
      },
      replace: true,
    }
  }

  if (to.name !== "moderator") {
    return
  }

  const weeklyActivitySubType = getWeeklyActivitySubType(to.params.params)
  if (weeklyActivitySubType && getForumToken()) {
    return resolveWeeklyActivityRoute(to, weeklyActivitySubType)
  }

  const forumId = getRouteForumId(to.params.params)
  setMeetingTrackContext({
    bizId: forumId,
    meetingInstanceId: forumId,
    joinRole: "moderator",
    joinRoleCode: 1,
    currentRole: "moderator",
    entryType: to.query.redirect ? "redirect_after_login" : "moderator_link",
    authState: getForumToken() ? "authenticated" : "anonymous",
    useMiniTrack: false,
  })
  beginMeetingJoinSession({ bizId: forumId, joinRole: "moderator" })
  trackMeetingPoint("meeting_join_page_view", {
    onceKey: getMeetingOnceKey("meeting_join_page_view", "moderator"),
    eventProperties: {
      has_cached_token: Boolean(getForumToken()),
    },
  })

  if (!getForumToken()) {
    trackMeetingPoint("meeting_join_auth_result", {
      eventProperties: {
        auth_stage: "employee_check",
        result: "denied",
        duration_ms: 0,
        failure_reason: "unauthorized",
      },
    })
    return getWelcomeRedirect(to)
  }

  const authStartedAt = Date.now()
  const authSession = captureForumSession()
  const accessStatus = await getEmployeeAccessStatus()
  if (accessStatus === "changed" || !isCurrentForumSession(authSession)) {
    return { path: to.path, query: to.query, hash: to.hash, replace: true }
  }

  if (accessStatus === "allowed") {
    setMeetingTrackContext({ authState: "authenticated" })
    trackMeetingPoint("meeting_join_auth_result", {
      eventProperties: {
        auth_stage: "employee_check",
        result: "success",
        duration_ms: Date.now() - authStartedAt,
      },
    })
    return
  }

  if (accessStatus === "expired") {
    setMeetingTrackContext({ authState: "expired" })
    trackMeetingPoint("meeting_join_auth_result", {
      eventProperties: {
        auth_stage: "employee_check",
        result: "failure",
        duration_ms: Date.now() - authStartedAt,
        failure_reason: "token_expired",
      },
    })
    return getWelcomeRedirect(to, { stripTokenQuery: true })
  }

  trackMeetingPoint("meeting_join_auth_result", {
    eventProperties: {
      auth_stage: "employee_check",
      result: "denied",
      duration_ms: Date.now() - authStartedAt,
      failure_reason: "employee_denied",
    },
  })
  return "/error"
})

export default router
